import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FlaskConical, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useWorkspace } from "@/context/WorkspaceContext";
import { errorMessage } from "@/lib/api";

export default function Onboarding({ embedded }) {
  const { api, reload, me } = useWorkspace();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", industry: "", country: "United Kingdom", website: "" });
  const [busy, setBusy] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const create = async (e) => {
    e.preventDefault();
    setBusy("create");
    try {
      const { data } = await api.post("/organisations", form);
      await reload(data.id);
      toast.success(`${data.name} created — you are its Organisation Administrator`);
      navigate("/");
    } catch (err) { toast.error(errorMessage(err)); setBusy(null); }
  };
  const joinDemo = async () => {
    setBusy("demo");
    try {
      const { data } = await api.post("/organisations/demo/join");
      await reload(data.organisation_id);
      navigate("/");
    } catch (err) { toast.error(errorMessage(err)); setBusy(null); }
  };

  return (
    <div className={`${embedded ? "" : "min-h-screen p-4 md:p-12"} flex items-start justify-center`}>
      <div className="w-full max-w-4xl fade-up">
        <p className="eyebrow mb-3">Welcome{me?.user?.name ? `, ${me.user.name}` : ""}</p>
        <h1 className="font-display text-4xl font-extrabold tracking-tight sm:text-5xl">Set up your workspace</h1>
        <p className="mt-3 max-w-xl text-sm text-muted-foreground">Each organisation is a fully isolated workspace with its own members, roles, CRM records and audit trail.</p>
        <div className="mt-10 grid gap-6 md:grid-cols-[1.4fr_1fr]">
          <form onSubmit={create} className="panel space-y-4 p-6" data-testid="create-org-form">
            <h2 className="font-display text-lg font-bold">Create an organisation</h2>
            {[["name", "Organisation name", true], ["industry", "Industry"], ["country", "Country"], ["website", "Website"]].map(([k, l, req]) => (
              <div key={k} className="space-y-1.5">
                <Label htmlFor={`org-${k}`} className="text-xs">{l}{req && <span className="text-primary"> *</span>}</Label>
                <Input id={`org-${k}`} value={form[k]} onChange={set(k)} required={req} minLength={req ? 2 : undefined} data-testid={`create-org-${k}-input`} />
              </div>
            ))}
            <Button type="submit" disabled={Boolean(busy)} className="w-full" data-testid="create-org-submit">{busy === "create" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Create organisation</Button>
          </form>
          {me?.demo_available && (
            <div className="panel flex flex-col gap-4 border-amber-500/30 p-6" data-testid="demo-workspace-card">
              <FlaskConical className="h-6 w-6 text-amber-500" />
              <h2 className="font-display text-lg font-bold">Explore the demo workspace</h2>
              <p className="text-sm text-muted-foreground">A shared, clearly labelled workspace containing only synthetic companies, contacts and opportunities. Nothing in it is real research.</p>
              <Button variant="outline" onClick={joinDemo} disabled={Boolean(busy)} className="mt-auto" data-testid="join-demo-button">{busy === "demo" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Open demo workspace</Button>
            </div>
          )}
        </div>
        <p className="mt-6 text-xs text-muted-foreground">Been invited by a colleague? Sign in with the invited email address and the workspace appears automatically.</p>
      </div>
    </div>
  );
}
